import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useCurrentGameId from '@/shared/hooks/useCurrentGameId';
import useServerStateContext from '@/shared/hooks/useServerStateContext';
import useRegistrationsQuery from '../hooks/useAutoIsoEntitiesQuery';
import { formatEndpoint } from '../../../util';
import { TrackedEntity, getEntityObjectSize, calculateEntityPosition } from './TrackedEntity';
import { CameraIcon, cameraSize } from './CameraIcon';
import { AutoIsoModel } from '../types/AutoIsoModel';
import { applyGhostFilter, createGhostFilterState } from '../utils/ghostFilter';
import { getGhostFilterConfig } from '../config/ghostFilter';
import useCurrentGameQuery from '@/features/games/hooks/useCurrentGameQuery';
import { useCurrentGame } from '@/features/games/context/CurrentGameContext';
import RinkTargetLayer from '@/features/framingPresets/components/RinkTargetLayer';
import { useTargetAiming } from '@/features/framingPresets/hooks/useTargetAiming';

// Internal feature flag - wire to LaunchDarkly later.
// Master switch for the ghost-entity filter in this view. When on, the
// per-league config (config/ghostFilter.ts) still decides whether a given
// league actually filters; when off, the filter is never invoked.
const ENABLE_GHOST_FILTER = true;

// Center-circle diameter in surface units, per league. The home team's logo is
// drawn inside it; leagues not listed get no logo.
const CENTER_CIRCLE_DIAMETER: Record<string, number> = {
  HOCKEY_NHL: 30,
  NBA: 12,
};
// Radius of the surface's rounded corners in surface units, per league, so a
// target set on the rink stays on the ice. Leagues not listed have square ones.
const CORNER_RADIUS: Record<string, number> = {
  HOCKEY_NHL: 28,
};
// Share of the circle the logo's box fills. At 1 the box spans the full
// diameter, so a logo can reach the line — square ones overlap it at the corners.
const CENTER_LOGO_RATIO = 1;

type Props = {
  sx?: any;
  // Reports the outer width this component needs for the surface to fill its
  // available height, so a parent can size itself to it.
  onFitWidthChange?: (width: number) => void;
};

const GamePositionsRepresentation = ({ sx, onFitWidthChange }: Props) => {
  const currentGameId = useCurrentGameId();
  const gameQuery = useCurrentGameQuery();
  const theme = useTheme();
  const { surfaceWidth, surfaceHeight, surfaceName, objectOfPlay, game, homeTeamLogoPath } =
    useCurrentGame();
  const { availableCameras, selectedCameraId, selectCamera, gameStateManager } =
    useServerStateContext();
  const { aiming } = useTargetAiming();

  const registrationsQuery = useRegistrationsQuery();
  const { lookup: registrationsLookup, homeTeam, awayTeam } = registrationsQuery?.data || {};

  const [top, setTop] = useState<string>();
  const [trackedEntities, setTrackedEntities] = useState<AutoIsoModel[]>([]);
  const surfaceImageUrl = formatEndpoint(`/v1.0/games/${currentGameId}/surface`);

  // Refs for imperative position updates (skip React re-renders)
  const entityElsRef = useRef<Map<string, HTMLElement>>(new Map());
  const prevEntityIdsRef = useRef<Set<string>>(new Set());
  // Freshest tracking snapshot, so the layout effect can place just-mounted
  // entities from live data (not the frozen pos stored in `trackedEntities`).
  const latestTrackingRef = useRef<AutoIsoModel[]>([]);

  // Ghost filter: persistent per-entity movement history (stateful across ticks)
  // and the active per-league config, kept in refs so the event handler created
  // inside the effect below always reads current values.
  const ghostFilterStateRef = useRef(createGhostFilterState());
  const ghostFilterConfig = useMemo(
    () => getGhostFilterConfig(game?.season?.league?.name),
    [game?.season?.league?.name],
  );
  const ghostFilterConfigRef = useRef(ghostFilterConfig);
  ghostFilterConfigRef.current = ghostFilterConfig;
  const registrationsLookupRef = useRef(registrationsLookup);
  registrationsLookupRef.current = registrationsLookup;

  // The surface fills whatever space the panel gives it, rather than rendering at
  // a fixed px size derived from a per-league constant. Measure the wrapper and
  // scale to fit, preserving aspect ratio.
  //
  // Only the RENDERED size is derived here — `surfaceWidth`/`surfaceHeight` stay
  // the coordinate basis, so entity and camera positions are computed from the
  // same normalized percentages as before.
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [available, setAvailable] = useState({ width: 0, height: 0, paddingX: 0 });

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      // Both sides unrounded — offsetWidth rounds to whole px, which would make
      // paddingX drift with the panel's fractional width.
      const paddingX = entry.borderBoxSize[0].inlineSize - entry.contentBoxSize[0].inlineSize;
      setAvailable((prev) =>
        prev.width === width && prev.height === height && prev.paddingX === paddingX
          ? prev
          : { width, height, paddingX },
      );
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!onFitWidthChange || !surfaceWidth || !surfaceHeight || !available.height) return;
    onFitWidthChange(
      Math.ceil((available.height * surfaceWidth) / surfaceHeight + available.paddingX),
    );
  }, [onFitWidthChange, surfaceWidth, surfaceHeight, available.height, available.paddingX]);

  const { gameTypeMetadataWidth, gameTypeMetadataHeight } = useMemo(() => {
    if (!surfaceWidth || !surfaceHeight || !available.width || !available.height) {
      return { gameTypeMetadataWidth: 0, gameTypeMetadataHeight: 0 };
    }
    // Fit on whichever axis runs out first, so the surface is never cropped.
    const scale = Math.min(available.width / surfaceWidth, available.height / surfaceHeight);
    return {
      gameTypeMetadataWidth: surfaceWidth * scale,
      gameTypeMetadataHeight: surfaceHeight * scale,
    };
  }, [surfaceWidth, surfaceHeight, available.width, available.height]);

  // Scales with the rendered surface, so the logo tracks the rink as it resizes.
  const centerCircleDiameter = CENTER_CIRCLE_DIAMETER[game?.season?.league?.name ?? ''];
  const centerLogoSize =
    centerCircleDiameter && surfaceWidth
      ? (gameTypeMetadataWidth * centerCircleDiameter * CENTER_LOGO_RATIO) / surfaceWidth
      : 0;

  // Store layout values in refs so the imperative updater always has current values
  const layoutRef = useRef({
    surfaceWidth,
    surfaceHeight,
    gameTypeMetadataWidth,
    gameTypeMetadataHeight,
  });
  layoutRef.current = {
    surfaceWidth,
    surfaceHeight,
    gameTypeMetadataWidth,
    gameTypeMetadataHeight,
  };

  const updateEntityPositions = useCallback((trackingEntities: AutoIsoModel[]) => {
    const {
      surfaceWidth: sw,
      surfaceHeight: sh,
      gameTypeMetadataWidth: w,
      gameTypeMetadataHeight: h,
    } = layoutRef.current;
    if (!w || !h || !sw || !sh) return;

    for (const entity of trackingEntities) {
      const el = entityElsRef.current.get(entity.id.toString());
      if (!el || !entity.pos) continue;

      const objectSize = getEntityObjectSize(entity.type);
      const { left, bottom } = calculateEntityPosition(entity.pos, w, h, sw, sh, objectSize);
      el.style.left = `${left}px`;
      el.style.bottom = `${bottom}px`;
    }
  }, []);

  const mergeWithRegistrations = useCallback((trackingEntities: AutoIsoModel[]) => {
    const lookup = registrationsLookupRef.current;
    if (!lookup) return [];

    return trackingEntities
      .map((autoIsoEntity) => {
        const entity = lookup[autoIsoEntity.id];
        if (!entity) return null;
        return { ...entity, ...autoIsoEntity };
      })
      .filter(Boolean) as AutoIsoModel[];
  }, []);

  useEffect(() => {
    if (!gameStateManager) return;

    const handleTrackingEntitiesChange = (trackingEntities: AutoIsoModel[]) => {
      if (!Array.isArray(trackingEntities)) return;

      // Ghost filter: drop stale, non-moving tracking markers before they reach
      // the render/position path. Must run every tick (it's stateful). Gated by
      // the feature flag; the per-league config decides whether it actually
      // filters (off → liveEntities === trackingEntities).
      const liveEntities = ENABLE_GHOST_FILTER
        ? applyGhostFilter(
            trackingEntities,
            ghostFilterConfigRef.current,
            ghostFilterStateRef.current,
          ).liveEntities
        : trackingEntities;

      latestTrackingRef.current = liveEntities;

      // Check if entity set changed (add/remove)
      const newIds = new Set(liveEntities.map((e) => e.id.toString()));
      const prevIds = prevEntityIdsRef.current;
      const hasEntityChanges =
        newIds.size !== prevIds.size || [...newIds].some((id) => !prevIds.has(id));

      if (hasEntityChanges) {
        prevEntityIdsRef.current = newIds;
        // Entities added/removed — need React to mount/unmount components
        const merged = mergeWithRegistrations(liveEntities);
        setTrackedEntities(merged);
      }

      // Always update positions imperatively (skips React re-render)
      updateEntityPositions(liveEntities);
    };

    // Initialize from last known state
    //@ts-ignore
    const initialEntities = (gameStateManager.lastGameState?.tracking.entities ||
      []) as AutoIsoModel[];
    if (Array.isArray(initialEntities) && initialEntities.length > 0) {
      const liveInitial = ENABLE_GHOST_FILTER
        ? applyGhostFilter(
            initialEntities,
            ghostFilterConfigRef.current,
            ghostFilterStateRef.current,
          ).liveEntities
        : initialEntities;
      const ids = new Set(liveInitial.map((e) => e.id.toString()));
      prevEntityIdsRef.current = ids;
      const merged = mergeWithRegistrations(liveInitial);
      latestTrackingRef.current = liveInitial;
      setTrackedEntities(merged);
      // Positions are applied by the layout effect below, before first paint.
    }

    gameStateManager.on('trackingEntitiesChange', handleTrackingEntitiesChange);

    return () => {
      if (gameStateManager.off)
        gameStateManager.off('trackingEntitiesChange', handleTrackingEntitiesChange);
    };
  }, [gameStateManager, mergeWithRegistrations, updateEntityPositions]);

  // Place entities synchronously after they mount (and when layout changes) but
  // BEFORE the browser paints, so a freshly-added entity never flashes at the
  // rink's top-left. Runs only on set/layout changes — not on the live position
  // stream (handled imperatively) nor on hover re-renders.
  useLayoutEffect(() => {
    updateEntityPositions(latestTrackingRef.current);
  }, [trackedEntities, gameTypeMetadataWidth, gameTypeMetadataHeight, updateEntityPositions]);

  const cameraPositions = useMemo(() => {
    if (!Array.isArray(availableCameras)) return [];
    if (!surfaceWidth || !surfaceHeight || !gameTypeMetadataWidth || !gameTypeMetadataHeight)
      return [];

    return availableCameras
      .filter((camera) => {
        // GameCamera objects have positionX, positionY instead of x, y
        const x = (camera as any).positionX ?? camera.x;
        const y = (camera as any).positionY ?? camera.y;
        const isLive = (camera as any).isLive ?? true; // Default to true if not specified

        return Number.isFinite(x) && Number.isFinite(y) && isLive;
      })
      .map((camera) => {
        // Use positionX/positionY from GameCamera or fallback to x/y
        const x = (camera as any).positionX ?? camera.x;
        const y = (camera as any).positionY ?? camera.y;

        // Calculate percentages with safety checks
        const xPercent = surfaceWidth ? (x / (surfaceWidth / 2) + 1) / 2.0 : 0.5;
        const yPercent = surfaceHeight ? (y / (surfaceHeight / 2) + 1) / 2.0 : 0.5;

        // Center the icon on its position so cameras on the boards sit on the
        // edge. Unlike entities, they aren't clamped inside the surface: the half
        // icon that overhangs lands in the wrapper's padding, not a scrollbar.
        const xVal = gameTypeMetadataWidth * xPercent - cameraSize / 2;
        const yVal = gameTypeMetadataHeight * yPercent - cameraSize / 2;

        // Ensure computed values are finite
        const computedX = Number.isFinite(xVal) ? xVal : 0;
        const computedY = Number.isFinite(yVal) ? yVal : 0;

        // Calculate angle from forward direction vector with custom mapping
        const forwardX = (camera as any).forwardX ?? 1;
        const forwardY = (camera as any).forwardY ?? 0;

        let calculatedAngle = 0;
        if (forwardY !== 0) {
          // Y component takes precedence
          calculatedAngle = forwardY > 0 ? 90 : 270; // (0,1,0) = 90°, (0,-1,0) = 270°
        } else {
          // Only X component
          calculatedAngle = forwardX >= 0 ? 0 : 180; // (1,0,0) = 0°, (-1,0,0) = 180°
        }

        return {
          ...camera,
          computedX,
          computedY,
          // Use existing angle property or calculate from forward vector
          angle: camera.angle ?? calculatedAngle,
        };
      });
  }, [
    availableCameras,
    surfaceWidth,
    surfaceHeight,
    gameTypeMetadataWidth,
    gameTypeMetadataHeight,
    cameraSize,
  ]);

  return (
    <Box
      ref={wrapperRef}
      sx={{
        width: '100%',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Wider sides: cameras sit past the end boards and overhang further than
        // the top/bottom ones. The measured paddingX widens the fit width to match.
        py: 2,
        px: 4,
        boxSizing: 'border-box',
        // Overhang (cameras off the boards) must never reach the Panel's
        // overflow:auto: its scrollbars shrink this box, which shrinks the fit
        // width, which drops the scrollbars — and the panel oscillates.
        overflow: 'hidden',
        ...sx,
      }}
    >
      {/* Rink: fixed-size, position:relative anchor for absolutely-positioned
          entities/cameras. Keep these dims in lockstep with updateEntityPositions. */}
      <Box
        sx={{
          touchAction: 'none',
          userSelect: 'none',
          width: gameTypeMetadataWidth,
          height: gameTypeMetadataHeight,
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <Box sx={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}>
          <img
            alt={surfaceName}
            src={surfaceImageUrl}
            style={{
              width: '100%',
              position: 'absolute',
            }}
          />
        </Box>

        {/* Home logo at center ice: under cameras and entities, never clickable.
            Empty alt so an unuploaded logo renders nothing, not a broken glyph. */}
        {centerLogoSize > 0 && homeTeamLogoPath && (
          <Box
            component="img"
            src={homeTeamLogoPath}
            alt=""
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: centerLogoSize,
              height: centerLogoSize,
              objectFit: 'contain',
              opacity: 0.3,
              pointerEvents: 'none',
            }}
          />
        )}

        {cameraPositions.map((camera) => (
          <CameraIcon
            key={camera.id}
            camera={camera}
            xVal={camera.computedX}
            yVal={camera.computedY}
            theme={theme}
            isActive={selectedCameraId === ((camera as any).camIndex ?? camera.idx)}
            onClick={() =>
              selectCamera((camera as any).camIndex ?? camera.idx, `Virtual ${surfaceName}`)
            }
            enabled={true}
            raised={aiming}
          />
        ))}

        {aiming && (
          <RinkTargetLayer
            surfaceWidth={surfaceWidth}
            surfaceHeight={surfaceHeight}
            width={gameTypeMetadataWidth}
            height={gameTypeMetadataHeight}
            cornerRadius={CORNER_RADIUS[game?.season?.league?.name ?? '']}
          />
        )}

        {trackedEntities &&
          trackedEntities.map((entity) => (
            <TrackedEntity
              key={entity.id}
              ref={(el) => {
                if (el) entityElsRef.current.set(entity.id.toString(), el);
                else entityElsRef.current.delete(entity.id.toString());
              }}
              entity={entity}
              objectOfPlay={objectOfPlay}
              awayTeam={awayTeam}
              homeTeam={homeTeam}
              game={gameQuery?.data}
              setTop={setTop}
              top={top}
            />
          ))}
      </Box>
    </Box>
  );
};

export default GamePositionsRepresentation;
